import { useSuspenseQueries } from '@tanstack/react-query';
import React from 'react';
import { AttritionStore } from '../../../pages/dashboard/attrition/attrition-store';
import { getPeopleBalanceSheetStoreInstance } from '../../../pages/dashboard/balance-sheet/dashboards/people-balance-sheet/people-balance-sheet-store';
import { getRecruitmentBalanceSheetStoreInstance } from '../../../pages/dashboard/balance-sheet/dashboards/recruitment-balance-sheet/recruitment-balance-sheet-store';
import { EvaluationStore } from '../../../pages/dashboard/evaluation/evaluation-store';
import { PayrollStore } from '../../../pages/dashboard/payroll-dashboard/payroll-store';
import { LiveSurveyService } from '../../../pages/dashboard/survey-dashboard/services/LiveSurveyService';
import { SurveyDashboardStoreClass } from '../../../pages/dashboard/survey-dashboard/survey-store';
import { AliasService as AliasServiceV1 } from '../../api/alias-service';
import { CachedAliasService } from '../../api/cached-alias-service';
import { CachedMasterDataService } from '../../api/cached-master-data-service';
import { NewBackendMasterDataApiService } from '../../api/master-data-service';
import { RequestService } from '../../api/request-service';
import { BenchmarkStore } from '../../benchmark/benchmark-store';
import { ChartDescriptionStore } from '../../chart-description/chart-description-store';
import { CompanySettingsStore } from '../../company-settings-store';
import { DashboardStore } from '../../dashboard-store';
import { DashboardSettingsStore } from '../../dashboard/dashboard-settings-store';
import { dateManagerService } from '../../date-manager/date-manager-service';
import { ErrorStore } from '../../error-store';
import { ExportStore } from '../../export-store';
import { FilterStore } from '../../filter/filter-store';
import { ForecastStore } from '../../forecast/forecast-store';
import { LoadingStateStore } from '../../loading-state-store';
import { MetricStore } from '../../metrics/metric-store';
import { PermissionBasedPermissionsStore } from '../../permissions/permissions-store';
import { RecruitmentStore } from '../../recruitment/recruitment-store';
import { ReportStore } from '../../report/report-store';
import { TimeSliderStore } from '../../timeslider/timeslider-store';
import { Hasher } from '../api/hasher';
import { hashString } from '../api/types';
import { useTimeSliderRangeStartDate } from '../common/components/timeslider/range-time-slider-hooks';
import { Months } from '../common/components/timeslider/types';
import {
  LegacyDomainScopedStoresContext,
  useAliasesContext,
  useAllUsedFieldsContext,
  useDashboardTrackerContext,
  useDomainContext,
  useDomainScopedErrorTrackerContext,
  useEffectivePermissionsContext,
  useEnvironmentServiceContext,
  useExecutorRoleContext,
  useExecutorRolePermissionsContext,
  useExecutorRolesContext,
  useExecutorRoleViewAuthorizationServiceContext,
  useGlobalLocaleContext,
  useGraphQlRequestServiceContext,
  useLatestEmployeeVersionContext,
  useLatestPreferencesContext,
  useLegacyStoresContext,
  useMetricTreeContext,
  useReactRouterHistoryContext,
  useSidebarTrackerContext,
  useSimulatedRoleContext,
  useUserRoleIdContext,
} from './contexts';
import { useMemoWithDeps } from './hooks';

const DebugPrefix = 'LegacyDomainScopedContextProvider';

const PreDependenciesLoader = ({
  children,
}: {
  children: (
    permissionsStore: PermissionBasedPermissionsStore,
    cachedAliasService: CachedAliasService
  ) => React.ReactNode;
}) => {
  const legacyStore = useLegacyStoresContext();
  const { permissionService } = legacyStore;
  const domain = useDomainContext();
  const domainScopedErrorTracker = useDomainScopedErrorTrackerContext();
  const userRoleId = useUserRoleIdContext();
  const effectivePermissions = useEffectivePermissionsContext();
  const executorRoles = useExecutorRolesContext();
  const graphQlRequestService = useGraphQlRequestServiceContext();
  const aliases = useAliasesContext();
  const simulatedRole = useSimulatedRoleContext();
  const executorRolePermissions = useExecutorRolePermissionsContext();

  const permissionsStoreQueryKey = [
    'permissions-store',
    domain,
    hashString([
      ...permissionService.getDeps(),
      Hasher.hash(executorRoles),
      simulatedRole?.id ?? userRoleId,
      Hasher.hash(effectivePermissions),
      Hasher.hash(executorRolePermissions),
      ...domainScopedErrorTracker.getDeps(),
    ]),
  ];
  const permissionsStoreQuery = {
    queryKey: permissionsStoreQueryKey,
    queryFn: () => {
      console.debug(
        `${DebugPrefix} - PermissionBasedPermissionsStore`,
        permissionsStoreQueryKey
      );
      const store = new PermissionBasedPermissionsStore(
        permissionService,
        executorRoles,
        simulatedRole?.id ?? userRoleId,
        effectivePermissions,
        executorRolePermissions,
        domain,
        domainScopedErrorTracker
      );
      store.checkMinimumPermissions();
      return store;
    },
  };

  const cachedAliasService: CachedAliasService = useMemoWithDeps(
    `${DebugPrefix} - cachedAliasService`,
    () =>
      new CachedAliasService(
        new AliasServiceV1(
          graphQlRequestService,
          aliases,
          simulatedRole?.id ?? null,
          domain
        )
      ),
    [domain, hashString([Hasher.hash(aliases), Hasher.hash(simulatedRole)])],
    [graphQlRequestService]
  );
  const results = useSuspenseQueries({
    queries: [permissionsStoreQuery],
  });

  const [permissionsStore] = [results[0].data];

  if (!permissionsStore) {
    throw new Error('Permissions store is undefined');
  }

  return <>{children(permissionsStore, cachedAliasService)}</>;
};

const LegacyDomainScopedContextProviderInner = ({
  children,
  permissionsStore,
  cachedAliasService,
}: {
  children: React.ReactNode;
  permissionsStore: PermissionBasedPermissionsStore;
  cachedAliasService: CachedAliasService;
}) => {
  const domain = useDomainContext();
  const executorRoleViewAuthorizationService =
    useExecutorRoleViewAuthorizationServiceContext();
  const allUsedFields = useAllUsedFieldsContext();
  const environmentService = useEnvironmentServiceContext();
  const simulatedRole = useSimulatedRoleContext();
  const latestPreferences = useLatestPreferencesContext();
  const graphQlRequestService = useGraphQlRequestServiceContext();
  const executorRole = useExecutorRoleContext();
  const domainScopedErrorTracker = useDomainScopedErrorTrackerContext();
  const dashboardTracker = useDashboardTrackerContext();
  const sidebarTracker = useSidebarTrackerContext();
  const locale = useGlobalLocaleContext();
  const selectedLanguage = locale.selected;
  const latestEmployeeVersion = useLatestEmployeeVersionContext();
  const metricTree = useMetricTreeContext();
  const aliases = useAliasesContext();

  const requestService = useMemoWithDeps(
    `${DebugPrefix} - requestService`,
    () => new RequestService(domainScopedErrorTracker, environmentService),
    [],
    [domainScopedErrorTracker, environmentService]
  );

  const companySettingsStore = useMemoWithDeps(
    `${DebugPrefix} - companySettingsStore`,
    () => new CompanySettingsStore(latestPreferences, domain),
    [Hasher.hash(latestPreferences), domain]
  );

  useMemoWithDeps(
    `${DebugPrefix} - dateManager`,
    () => {
      const firstMonthOfYear =
        latestPreferences.finYearStartMonth ?? Months.January;
      dateManagerService.initialize(selectedLanguage, firstMonthOfYear);
    },
    [Hasher.hash(selectedLanguage), Hasher.hash(latestPreferences)],
    []
  );

  const metricStore = useMemoWithDeps(
    `${DebugPrefix} - metricStore`,
    () => {
      const store = new MetricStore(
        permissionsStore,
        companySettingsStore,
        selectedLanguage,
        metricTree
      );
      return store;
    },
    [Hasher.hash(metricTree), selectedLanguage],
    [permissionsStore, companySettingsStore]
  );

  const reportStore = useMemoWithDeps(
    `${DebugPrefix} - reportStore`,
    () => new ReportStore(executorRoleViewAuthorizationService, allUsedFields),
    [Hasher.hash(allUsedFields)],
    [executorRoleViewAuthorizationService]
  );
  const recruitmentStore = useMemoWithDeps(
    `${DebugPrefix} - recruitmentStore`,
    () =>
      new RecruitmentStore(executorRoleViewAuthorizationService, allUsedFields),
    [Hasher.hash(allUsedFields)],
    [executorRoleViewAuthorizationService]
  );
  const exportStore = useMemoWithDeps(
    `${DebugPrefix} - exportStore`,
    () => new ExportStore(dashboardTracker),
    [],
    [dashboardTracker]
  );
  const dashboardSettingsStore = useMemoWithDeps(
    `${DebugPrefix} - dashboardSettingsStore`,
    () => new DashboardSettingsStore(sidebarTracker),
    [],
    [sidebarTracker]
  );
  // TODO: for dashboard specific stores we probably want to create them only in the specific dashboard and pass them as context
  // keeping things here for simplicity now

  const evaluationStore = useMemoWithDeps(
    `${DebugPrefix} - evaluationStore`,
    () => new EvaluationStore(companySettingsStore),
    [],
    [companySettingsStore]
  );

  const loadingStateStore = useMemoWithDeps(
    `${DebugPrefix} - loadingStateStore`,
    () => new LoadingStateStore(),
    []
  );

  const timeSliderStartDate = useTimeSliderRangeStartDate();

  const timeSliderStore = useMemoWithDeps(
    `${DebugPrefix} - timeSliderStore`,
    () => {
      const firstMonthOfYear =
        latestPreferences.finYearStartMonth ?? Months.January;
      const store = new TimeSliderStore();
      store.setUpTimeSlider(
        latestEmployeeVersion,
        firstMonthOfYear,
        timeSliderStartDate
      );
      return store;
    },
    [
      latestEmployeeVersion,
      timeSliderStartDate,
      Hasher.hash(latestPreferences),
    ],
    []
  );

  const benchmarkStore = useMemoWithDeps(
    `${DebugPrefix} - benchmarkStore`,
    () => new BenchmarkStore(timeSliderStore),
    [],
    [timeSliderStore]
  );

  const forecastStore = useMemoWithDeps(
    `${DebugPrefix} - forecastStore`,
    () => {
      return new ForecastStore(timeSliderStore);
    },
    [],
    [timeSliderStore]
  );

  const dashboardStore = useMemoWithDeps(
    `${DebugPrefix} - dashboardStore`,
    () => {
      return new DashboardStore();
    },
    []
  );

  const attritionStore = useMemoWithDeps(
    `${DebugPrefix} - attritionStore`,
    () => {
      return new AttritionStore(timeSliderStore);
    },
    [],
    [timeSliderStore]
  );

  const chartDescriptionStore = useMemoWithDeps(
    `${DebugPrefix} - chartDescriptionStore`,
    () => {
      return new ChartDescriptionStore();
    },
    []
  );

  const errorStore = useMemoWithDeps(
    `${DebugPrefix} - errorStore`,
    () => {
      return new ErrorStore();
    },
    []
  );

  const masterDataApiService = useMemoWithDeps(
    `${DebugPrefix} - masterDataApiService executorRole=${executorRole.id}, simulatedRole=${
      simulatedRole?.id ?? 'null'
    }`,
    () =>
      new NewBackendMasterDataApiService(
        graphQlRequestService,
        requestService,
        permissionsStore,
        environmentService,
        executorRole,
        simulatedRole,
        domainScopedErrorTracker,
        errorStore
      ),
    [hashString([Hasher.hash(executorRole), Hasher.hash(simulatedRole)])],
    [
      graphQlRequestService,
      requestService,
      permissionsStore,
      environmentService,
      domainScopedErrorTracker,
      errorStore,
    ]
  );

  const cachedMasterDataApiService = useMemoWithDeps(
    `${DebugPrefix} - cachedMasterDataApiService domain=${domain}, executorRole=${executorRole.id}, simulatedRole=${
      simulatedRole?.id ?? 'null'
    }`,
    () =>
      new CachedMasterDataService(
        masterDataApiService,
        domain,
        executorRole,
        simulatedRole
      ),
    [
      domain,
      hashString([Hasher.hash(executorRole), Hasher.hash(simulatedRole)]),
    ],
    [masterDataApiService]
  );

  const filterStore = useMemoWithDeps(
    `${DebugPrefix} - filterStore`,
    () => {
      const filterStore = new FilterStore(
        benchmarkStore,
        timeSliderStore,
        cachedMasterDataApiService
      );
      void filterStore.loadEmployeeData(latestEmployeeVersion);
      return filterStore;
    },
    [latestEmployeeVersion],
    [benchmarkStore, timeSliderStore, cachedMasterDataApiService]
  );

  const peopleBalanceSheetStore = useMemoWithDeps(
    `${DebugPrefix} - peopleBalanceSheetStore`,
    () =>
      getPeopleBalanceSheetStoreInstance(
        metricStore,
        aliases,
        selectedLanguage
      ),
    [hashString([Hasher.hash(aliases), Hasher.hash(selectedLanguage)])],
    [metricStore]
  );

  const recruitmentBalanceSheetStore = useMemoWithDeps(
    `${DebugPrefix} - recruitmentBalanceSheetStore`,
    () =>
      getRecruitmentBalanceSheetStoreInstance(
        metricStore,
        aliases,
        selectedLanguage
      ),
    [hashString([Hasher.hash(aliases), Hasher.hash(selectedLanguage)])],
    [metricStore]
  );

  const surveyService = useMemoWithDeps(
    `${DebugPrefix} - surveyService`,
    () => new LiveSurveyService(cachedMasterDataApiService),
    [],
    [cachedMasterDataApiService]
  );

  const surveyDashboardStore = useMemoWithDeps(
    `${DebugPrefix} - surveyDashboardStore`,
    () => new SurveyDashboardStoreClass(surveyService),
    [],
    [surveyService]
  );

  const payrollStore = useMemoWithDeps(
    `${DebugPrefix} - payrollStore`,
    () => new PayrollStore(timeSliderStore, cachedMasterDataApiService),
    [],
    [timeSliderStore, cachedMasterDataApiService]
  );

  const legacyDomainScopedStore = useMemoWithDeps(
    `${DebugPrefix} - legacyDomainScopedStore`,
    () => ({
      permissionsStore,
      companySettingsStore,
      metricStore,
      cachedAliasService,
      cachedMasterDataApiService,
      reportStore,
      recruitmentStore,
      masterDataApiService,
      exportStore,
      dashboardSettingsStore,
      evaluationStore,
      requestService,
      timeSliderStore,
      benchmarkStore,
      filterStore,
      forecastStore,
      loadingStateStore,
      dashboardStore,
      attritionStore,
      chartDescriptionStore,
      errorStore,
      peopleBalanceSheetStore,
      recruitmentBalanceSheetStore,
      surveyDashboardStore,
      surveyService,
      payrollStore,
    }),
    [],
    [
      permissionsStore,
      companySettingsStore,
      metricStore,
      cachedAliasService,
      cachedMasterDataApiService,
      reportStore,
      recruitmentStore,
      masterDataApiService,
      exportStore,
      dashboardSettingsStore,
      evaluationStore,
      requestService,
      timeSliderStore,
      benchmarkStore,
      filterStore,
      forecastStore,
      loadingStateStore,
      dashboardStore,
      attritionStore,
      chartDescriptionStore,
      errorStore,
      peopleBalanceSheetStore,
      recruitmentBalanceSheetStore,
      surveyDashboardStore,
      surveyService,
      payrollStore,
    ]
  );

  const browserHistory = useReactRouterHistoryContext();
  browserHistory.listen(() => {
    requestService.staleRequestsMap.forEach((request) =>
      request.abort('Request is stale')
    );
  });

  return (
    <LegacyDomainScopedStoresContext.Provider value={legacyDomainScopedStore}>
      {children}
    </LegacyDomainScopedStoresContext.Provider>
  );
};

export const LegacyDomainScopedContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <PreDependenciesLoader>
      {(permissionsStore, cachedAliasService) => (
        <LegacyDomainScopedContextProviderInner
          permissionsStore={permissionsStore}
          cachedAliasService={cachedAliasService}
        >
          {children}
        </LegacyDomainScopedContextProviderInner>
      )}
    </PreDependenciesLoader>
  );
};
